import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Mail, CheckCircle2, RotateCcw, User, Clock, StickyNote, Loader2, Send, Reply, MailCheck } from 'lucide-react';
import { t } from '../../lib/i18n';

const formatDate = (value, language) => (value
  ? new Date(value).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '-');

const RepliesThread = ({ replies, language }) => {
  if (!replies?.length) return null;
  return (
    <div className="space-y-2" data-testid="support-replies-thread">
      <p className="text-[10px] uppercase tracking-wider text-white/40 flex items-center gap-1"><MailCheck className="w-3 h-3 text-[var(--lux-gold)]" /> {t('replies', language)} ({replies.length})</p>
      {replies.map(r => (
        <div key={r.id} className="border-l-2 border-lux-gold/50 pl-3 py-1">
          <p className="text-sm text-white/80 whitespace-pre-wrap">{r.body}</p>
          <p className="text-[11px] text-white/35 mt-1">{formatDate(r.sent_at, language)} · {t('sentTo', language)} {r.to} · {r.sent_by}</p>
        </div>
      ))}
    </div>
  );
};

export const SupportMessageCard = ({ message, language, onUpdate, onReply }) => {
  const [note, setNote] = useState(message.admin_note || '');
  const [busy, setBusy] = useState(false);
  const [showNote, setShowNote] = useState(Boolean(message.admin_note));
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [markHandled, setMarkHandled] = useState(true);
  const [sending, setSending] = useState(false);
  const handled = message.status === 'handled';
  const replyTo = message.contact_email || (!message.is_guest ? message.user_email : null);
  const mailto = replyTo
    ? `mailto:${replyTo}?subject=${encodeURIComponent(`Re: ${message.subject} [#${message.ticket_ref}]`)}`
    : null;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const handleSendReply = async () => {
    if (replyBody.trim().length < 2 || sending) return;
    setSending(true);
    try {
      await onReply(message.id, replyBody.trim(), markHandled);
      setReplyBody('');
      setShowReply(false);
    } catch (err) {
      // toast handled by parent
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className={`bg-lux-panel/90 border-white/[0.08] fade-in ${handled ? 'opacity-80' : ''}`} data-testid={`support-message-${message.ticket_ref}`}>
      <CardContent className="py-4 space-y-3">
        <div className="flex flex-wrap items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] text-[var(--lux-amber)] bg-lux-gold/10 border border-lux-gold/20 px-1.5 py-0.5 rounded">#{message.ticket_ref}</span>
              <h3 className="text-sm font-semibold text-white truncate" data-testid="support-message-subject">{message.subject}</h3>
              <Badge variant="outline" className={handled ? 'border-white/15 text-white/50' : 'border-lux-gold/40 text-[var(--lux-amber)]'} data-testid="support-message-status">
                {handled ? t('badgeHandled', language) : t('badgeNew', language)}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-white/40">
              <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {message.user_name || '-'}{message.is_guest ? ` (${t('guestUser', language)})` : ''}</span>
              <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {replyTo || t('noContactEmail', language)}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(message.created_at, language)}</span>
              <span className="uppercase tracking-wider bg-white/5 px-1.5 py-0.5 rounded">{message.language}</span>
            </div>
          </div>
        </div>

        <p className="text-sm text-white/80 whitespace-pre-wrap bg-black/20 border border-white/5 rounded-lg p-3" data-testid="support-message-body">{message.message}</p>

        {handled && message.handled_by && (
          <p className="text-xs text-white/40">{t('handledBy', language)} {message.handled_by} · {formatDate(message.handled_at, language)}</p>
        )}

        <RepliesThread replies={message.replies} language={language} />

        {showReply && (
          <div className="space-y-2 p-3 rounded-lg bg-lux-gold/5 border border-lux-gold/20" data-testid="support-reply-form">
            <Textarea
              data-testid="support-reply-input"
              value={replyBody}
              onChange={e => setReplyBody(e.target.value.slice(0, 5000))}
              placeholder={t('replyPlaceholder', language)}
              rows={4}
              autoFocus
              className="bg-black/20 border-white/10 text-white placeholder:text-white/30 resize-none text-sm"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Checkbox id={`mark-${message.id}`} data-testid="support-reply-mark-handled" checked={markHandled} onCheckedChange={(v) => setMarkHandled(Boolean(v))} className="border-white/30 data-[state=checked]:bg-[var(--lux-gold)] data-[state=checked]:border-[var(--lux-gold)] data-[state=checked]:text-[var(--lux-ink)]" />
                <Label htmlFor={`mark-${message.id}`} className="text-xs text-white/60 cursor-pointer">{t('replyMarkHandled', language)}</Label>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-white/30 tabular-nums">{replyBody.length}/5000</span>
                <Button size="sm" variant="ghost" onClick={() => setShowReply(false)} className="text-white/50 hover:text-white h-8 text-xs" data-testid="support-reply-cancel">{t('consentCancel', language)}</Button>
                <Button size="sm" onClick={handleSendReply} disabled={sending || replyBody.trim().length < 2} className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] h-8 text-xs" data-testid="support-reply-send">
                  {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} {sending ? t('replySending', language) : t('replySend', language)}
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-white/35">{t('sentTo', language)} <span className="text-white/60">{replyTo}</span></p>
          </div>
        )}

        {showNote && (
          <div className="space-y-2">
            <Textarea
              data-testid="support-admin-note-input"
              value={note}
              onChange={e => setNote(e.target.value.slice(0, 1000))}
              placeholder={t('adminNotePlaceholder', language)}
              rows={2}
              className="bg-black/20 border-white/10 text-white placeholder:text-white/30 resize-none text-sm"
            />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => onUpdate(message.id, message.status, note))} className="bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)] h-8 text-xs" data-testid="support-save-note-button">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <StickyNote className="w-3 h-3 mr-1" />} {t('saveNote', language)}
            </Button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {replyTo && !showReply && (
            <Button size="sm" onClick={() => setShowReply(true)} className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] h-8 text-xs" data-testid="support-reply-button">
              <Reply className="w-3 h-3 mr-1" /> {t('reply', language)}
            </Button>
          )}
          {handled ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => onUpdate(message.id, 'new'))} className="bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)] h-8 text-xs" data-testid="support-reopen-button">
              <RotateCcw className="w-3 h-3 mr-1" /> {t('reopen', language)}
            </Button>
          ) : (
            <Button size="sm" variant={replyTo ? 'outline' : 'default'} disabled={busy} onClick={() => run(() => onUpdate(message.id, 'handled'))} className={replyTo ? 'bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)] h-8 text-xs' : 'bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] h-8 text-xs'} data-testid="support-mark-handled-button">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} {t('markHandled', language)}
            </Button>
          )}
          {mailto && (
            <Button size="sm" variant="outline" asChild className="bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)] hover:text-[var(--lux-gold)] h-8 text-xs">
              <a href={mailto} data-testid="support-reply-email-link"><Mail className="w-3 h-3 mr-1" /> {t('replyByEmail', language)}</a>
            </Button>
          )}
          {!showNote && (
            <Button size="sm" variant="ghost" onClick={() => setShowNote(true)} className="text-white/40 hover:text-white h-8 text-xs" data-testid="support-add-note-button">
              <StickyNote className="w-3 h-3 mr-1" /> {t('adminNote', language)}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
